import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Logo */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/" className="inline-flex items-center">
            <img src="/qnc-logo.png" alt="QNC Co. Limited" className="h-12 w-auto" />
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-8">Last Updated: November 2025</p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                QNC Co. Limited is committed to protecting your personal information in accordance with Jamaica's Data Protection Act, 2020.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We may collect the following types of information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Personal Information:</strong> Name, email address, phone number, and mailing address</li>
                <li><strong>Technical Information:</strong> IP address, browser type, device information, and cookies</li>
                <li><strong>Project-Related Information:</strong> Project details, requirements, site photos, and specifications</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use your data to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide estimates and quotes for construction services</li>
                <li>Communicate with you about your projects and inquiries</li>
                <li>Schedule site visits and service appointments</li>
                <li>Improve our services and website functionality</li>
                <li>Comply with legal and regulatory requirements</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Legal Basis for Processing</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We process your data based on:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Consent:</strong> You have given us permission to process your information</li>
                <li><strong>Contract Performance:</strong> Processing is necessary to fulfill our services</li>
                <li><strong>Legal Obligations:</strong> We must process data to comply with the law</li>
                <li><strong>Legitimate Business Interest:</strong> Processing is necessary for our business operations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Sharing of Information</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We may share your information with:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Employees and contractors involved in your project</li>
                <li>Suppliers and equipment vendors as necessary for service delivery</li>
                <li>Law enforcement or regulatory authorities when legally required</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                We do not sell or rent your personal information to third parties.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your data only as long as necessary for service delivery, legal compliance, and business record-keeping. Project-related data may be retained for up to seven (7) years in accordance with Jamaican business record requirements.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Protection & Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We use reasonable technical and organizational measures to secure your information, including encrypted connections, secure servers, and restricted access to personal data. However, no method of transmission over the internet is 100% secure.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Your Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Under Jamaica's Data Protection Act, you have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal obligations)</li>
                <li><strong>Withdraw Consent:</strong> Withdraw your consent to data processing at any time</li>
                <li><strong>File a Complaint:</strong> Lodge a complaint with the Office of the Information Commissioner (OIC)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Cookies</h2>
              <p className="text-gray-700 leading-relaxed">
                We may use cookies to improve website functionality and user experience. You can disable cookies through your browser settings, but this may affect certain features of our website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our services and website are not intended for individuals under 18. We do not knowingly collect personal information from children.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Updates to Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this policy periodically. Continued use of our website indicates acceptance of any changes. The "Last Updated" date at the top of this page reflects the most recent revision.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Information</h2>
              <div className="bg-blue-50 p-6 rounded-lg">
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy or wish to exercise your data protection rights, please contact us:
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  <strong>QNC Co. Limited</strong>
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  141B Constant Spring Rd, Kingston, Jamaica
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Email: <a href="mailto:qncconstructions@gmail.com" className="text-blue-600 hover:underline">qncconstructions@gmail.com</a>
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Phone: 876-479-7139 / 876-784-7936
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;